// PRD.329 — Laboratory Requisition: catalog grouping, requisition metadata and
// the pure write PLAN for `createLabRequisition`.
//
// Pure and free of workspace-package imports so it runs under node:test. The
// plan is plain data built BEFORE the write transaction; the web-admin writer
// executes it through the transaction executor only (PRD.329 B1 constraint).
//
// One requisition = ONE `lab_orders` row + N `lab_order_tests` + (when billing
// is available) ONE dedicated invoice with N lines. Tests from several
// departments share the one order — departments group the picker, they never
// split orders.

import {
  LAB_TEST_INVOICE_SOURCE_TYPE,
  labInvoiceItemId,
  isSelectableLabTest,
  resolveCatalogDepartment,
  initialLabFulfillment,
  type LabCatalogEntry,
  type LabOrderTestDraft,
} from './laboratory-workflow.js';

// ─── Priority ───

export type LabPriority = 'routine' | 'emergency';

export const LAB_PRIORITIES: readonly LabPriority[] = ['routine', 'emergency'] as const;

export const LAB_PRIORITY_LABELS: Record<LabPriority, string> = {
  routine: 'Routine',
  emergency: 'Emergency',
};

/** NULL / unknown reads as routine — every pre-PRD.329 order is routine. */
export function normalizeLabPriority(value: unknown): LabPriority {
  return value === 'emergency' ? 'emergency' : 'routine';
}

// ─── Catalog grouping (the requisition picker) ───

/** Group key of the virtual "Other / Unassigned" section. Never a DB row. */
export const UNASSIGNED_LAB_DEPARTMENT_KEY = '__unassigned__';
export const UNASSIGNED_LAB_DEPARTMENT_LABEL = 'Other / Unassigned';

/** A catalog row with its department joined — `LabTestCatalogRow` satisfies this. */
export interface RequisitionCatalogEntry extends LabCatalogEntry {
  department_sort_order?: number | null;
}

export interface RequisitionCatalogGroup<T extends RequisitionCatalogEntry> {
  /** Department id, or `UNASSIGNED_LAB_DEPARTMENT_KEY`. */
  key: string;
  departmentId: string | null;
  label: string;
  tests: T[];
}

function byName(a: { name?: string | null }, b: { name?: string | null }): number {
  return (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });
}

/** Case-insensitive match on service name or `external_code`. Blank matches all. */
export function matchesRequisitionSearch(row: RequisitionCatalogEntry, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return (
    (row.name ?? '').toLowerCase().includes(q) ||
    (row.external_code ?? '').toLowerCase().includes(q)
  );
}

/**
 * Group the orderable catalog by ACTIVE department for the requisition picker.
 *
 * - Only `isSelectableLabTest` rows appear: lab test + service active + (no
 *   department, or an active one). Disabled departments and their services
 *   vanish from NEW requisitions only; history is untouched.
 * - Departments order by `sort_order`, then name; services by name (R7).
 *   `external_code` never influences order — it is identity, not presentation.
 * - Unassigned services (including ones whose department row is not available
 *   locally) form a final virtual "Other / Unassigned" group, so legacy lab
 *   services never disappear after migration.
 * - Search narrows what is SHOWN; empty groups are dropped. Selection state is
 *   owned by the caller, so filtering never loses a selection.
 */
export function groupRequisitionCatalog<T extends RequisitionCatalogEntry>(
  rows: readonly T[],
  search = '',
): RequisitionCatalogGroup<T>[] {
  const groups = new Map<string, RequisitionCatalogGroup<T> & { sort: number }>();
  const unassigned: T[] = [];

  for (const row of rows) {
    if (!isSelectableLabTest(row)) continue;
    if (!matchesRequisitionSearch(row, search)) continue;
    const department = resolveCatalogDepartment(row);
    if (!department) {
      unassigned.push(row);
      continue;
    }
    let group = groups.get(department.id);
    if (!group) {
      group = {
        key: department.id,
        departmentId: department.id,
        label: department.name,
        tests: [],
        sort: row.department_sort_order ?? 0,
      };
      groups.set(department.id, group);
    }
    group.tests.push(row);
  }

  const ordered = [...groups.values()]
    .sort(
      (a, b) =>
        a.sort - b.sort || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )
    .map(({ sort: _sort, ...g }) => ({ ...g, tests: [...g.tests].sort(byName) }));

  if (unassigned.length > 0) {
    ordered.push({
      key: UNASSIGNED_LAB_DEPARTMENT_KEY,
      departmentId: null,
      label: UNASSIGNED_LAB_DEPARTMENT_LABEL,
      tests: [...unassigned].sort(byName),
    });
  }
  return ordered;
}

// ─── Department tabs (the requisition picker's pill bar) ───

/** Key of the "All departments" pill. Never a department id. */
export const ALL_LAB_DEPARTMENTS_TAB = '__all__';

export interface RequisitionDepartmentTab {
  /** `ALL_LAB_DEPARTMENTS_TAB`, a department id, or `UNASSIGNED_LAB_DEPARTMENT_KEY`. */
  key: string;
  label: string;
  /** Orderable tests in the tab (unaffected by search). */
  total: number;
  /** How many of them are currently selected. */
  selected: number;
}

/**
 * Pill tabs for the requisition picker: "All" first, then one per group in
 * `groupRequisitionCatalog` order (departments by sort_order, then
 * Other / Unassigned). Pass UNSEARCHED groups so counts stay stable while the
 * user types. Selection is counted per tab, so a pill shows where the
 * selected tests are even when that department isn't the active tab.
 */
export function requisitionDepartmentTabs(
  groups: readonly RequisitionCatalogGroup<RequisitionCatalogEntry>[],
  selectedIds: ReadonlySet<string>,
): RequisitionDepartmentTab[] {
  const tabs = groups.map((g) => ({
    key: g.key,
    label: g.label,
    total: g.tests.length,
    selected: g.tests.filter((t) => selectedIds.has(t.id)).length,
  }));
  return [
    {
      key: ALL_LAB_DEPARTMENTS_TAB,
      label: 'All',
      total: tabs.reduce((n, t) => n + t.total, 0),
      selected: tabs.reduce((n, t) => n + t.selected, 0),
    },
    ...tabs,
  ];
}

// ─── Historical department display (R2) ───

/** The ordered-test fields the department badge reads. */
export interface LabTestDepartmentSnapshotSource {
  laboratory_department_id?: string | null;
  laboratory_department_name_snapshot?: string | null;
}

/**
 * The department badge for an ORDERED test — from its snapshot only.
 *
 * A NULL snapshot (free-text test, unassigned service, or a pre-PRD.329 row)
 * renders NO badge and never falls back to the service's current department:
 * reassigning a service must not reclassify history (PRD.329 R2).
 */
export function labTestDepartmentBadge(
  test: LabTestDepartmentSnapshotSource,
): { id: string; name: string } | null {
  const id = test.laboratory_department_id;
  const name = test.laboratory_department_name_snapshot?.trim();
  if (!id || !name) return null;
  return { id, name };
}

// ─── Deterministic identity (Layer 1) ───

/**
 * Ids derived from the requisition attempt's `requisitionId` (minted once when
 * the drawer opens and kept until success). A RETRY of the same attempt
 * therefore re-derives identical ids and converges instead of duplicating.
 *
 * Scope: this does NOT deduplicate two independently created requisitions —
 * each drawer session mints its own `requisitionId`. Cross-device semantic
 * duplicate detection is out of scope (PRD.329 round 3).
 */
export function labRequisitionTestId(
  orderId: string,
  draft: Pick<LabOrderTestDraft, 'serviceId'>,
  index: number,
): string {
  return draft.serviceId ? `${orderId}-svc-${draft.serviceId}` : `${orderId}-ft-${index}`;
}

/** The requisition's DEDICATED invoice (PRD.329 D4 — never an unrelated open one). */
export function labRequisitionInvoiceId(orderId: string): string {
  return `labinv-${orderId}`;
}

// ─── The write plan ───

export interface LabRequisitionPlanInput {
  requisitionId: string;
  orgId: string;
  clinicId?: string | null;
  patientId: string;
  patientName: string;
  visitId?: string | null;
  encounterId?: string | null;
  appointmentId?: string | null;
  queueId?: string | null;
  orderingProviderId?: string | null;
  referringDoctorName?: string | null;
  priority?: LabPriority | null;
  /** Brief clinical details → `lab_orders.notes`. */
  clinicalNotes?: string | null;
  source: 'consultation' | 'patient_workspace' | 'laboratory';
  actor: { uid: string; name?: string | null };
  drafts: readonly LabOrderTestDraft[];
  /**
   * Whether to create the dedicated invoice. False when the Invoice module is
   * off or the user lacks Billing (PRD.329 D6): the order is still created.
   */
  bill: boolean;
  /** Requisition date/time (ISO) → `lab_orders.ordered_at`. */
  orderedAt: string;
  /** Invoice date, YYYY-MM-DD. */
  invoiceDate: string;
  /** Write timestamp (ISO) for created_at / updated_at. */
  now: string;
}

type Row = Record<string, string | number | null>;

export interface LabRequisitionPlan {
  orderId: string;
  invoiceId: string | null;
  /** `lab_orders` columns; `order_number` is assigned by the writer. */
  order: Row;
  tests: Row[];
  /** `invoices` columns; `invoice_no` and totals are assigned by the writer. */
  invoice: Row | null;
  invoiceItems: Row[];
}

function blankToNull(v: string | null | undefined): string | null {
  const t = (v ?? '').trim();
  return t ? t : null;
}

/**
 * Build every row a requisition writes, as plain data.
 *
 * Invoice lines mirror `billLabTests` exactly — deterministic `lab-<testId>`
 * id, `source_type = 'lab_test'`, `source_id = test id`, price from the
 * ordering-time snapshot — so Laboratory Flow's derived Pay step, Billing and
 * `unbilledLabTestIds` read requisition lines identically. Totals are NOT
 * computed here: the writer recomputes them with the shared invoice summary,
 * so there is no parallel laboratory total.
 */
export function buildLabRequisitionPlan(input: LabRequisitionPlanInput): LabRequisitionPlan {
  if (input.drafts.length === 0) {
    throw new Error('A laboratory requisition needs at least one test.');
  }
  const orderId = input.requisitionId;
  const now = input.now;

  const order: Row = {
    id: orderId,
    org_id: input.orgId,
    clinic_id: input.clinicId ?? null,
    patient_id: input.patientId,
    visit_id: input.visitId ?? null,
    encounter_id: input.encounterId ?? null,
    appointment_id: input.appointmentId ?? null,
    queue_id: input.queueId ?? null,
    ordering_provider_id: input.orderingProviderId ?? null,
    ordered_by: input.actor.uid,
    ordered_by_name: input.actor.name ?? null,
    source: input.source,
    notes: blankToNull(input.clinicalNotes),
    priority: normalizeLabPriority(input.priority),
    referring_doctor_name: blankToNull(input.referringDoctorName),
    fulfillment: initialLabFulfillment(input.source),
    ordered_at: input.orderedAt,
    created_at: now,
    updated_at: now,
  };

  const seen = new Set<string>();
  const tests: Row[] = [];
  input.drafts.forEach((draft, index) => {
    const id = labRequisitionTestId(orderId, draft, index);
    if (seen.has(id)) return; // one test per service per requisition
    seen.add(id);
    tests.push({
      id,
      org_id: input.orgId,
      lab_order_id: orderId,
      patient_id: input.patientId,
      service_id: draft.serviceId,
      test_name: draft.name,
      test_code_snapshot: draft.code ?? null,
      price_cents_snapshot: draft.priceCents ?? null,
      laboratory_department_id: draft.departmentId ?? null,
      laboratory_department_name_snapshot: draft.departmentId
        ? (draft.departmentName ?? null)
        : null,
      status: 'ordered',
      sort_order: tests.length,
      notes: blankToNull(draft.notes),
      created_at: now,
      updated_at: now,
    });
  });

  if (!input.bill) {
    return { orderId, invoiceId: null, order, tests, invoice: null, invoiceItems: [] };
  }

  const invoiceId = labRequisitionInvoiceId(orderId);
  const invoice: Row = {
    id: invoiceId,
    org_id: input.orgId,
    clinic_id: input.clinicId ?? null,
    patient_id: input.patientId,
    encounter_id: input.encounterId ?? null,
    visit_id: input.visitId ?? null,
    patient_name: input.patientName,
    date_iso: input.invoiceDate,
    status: 'draft',
    notes: '',
    created_at: now,
    updated_at: now,
  };

  const invoiceItems: Row[] = tests.map((t, i) => {
    const cents = Number(t.price_cents_snapshot ?? 0);
    return {
      id: labInvoiceItemId(String(t.id)),
      org_id: input.orgId,
      invoice_id: invoiceId,
      service_name: String(t.test_name ?? 'Laboratory test'),
      service_id: t.service_id,
      qty: 1,
      unit_price: Number.isFinite(cents) ? cents / 100 : 0,
      discount: 0,
      discount_type: 'fixed',
      tax_rate: 0,
      source_type: LAB_TEST_INVOICE_SOURCE_TYPE,
      source_id: String(t.id),
      add_to_treatment: 0,
      payment_status: 'unpaid',
      sort_order: i,
      created_at: now,
      updated_at: now,
    };
  });

  return { orderId, invoiceId, order, tests, invoice, invoiceItems };
}

/** Tests the Review step must flag: selected but with no catalog price. */
export function unpricedRequisitionDrafts(
  drafts: readonly LabOrderTestDraft[],
): LabOrderTestDraft[] {
  return drafts.filter((d) => d.priceCents == null);
}
